import {course_getCourseById_Api} from 'apis'
import {getId} from 'apis/model/base'
import {UseHook} from 'components/UseHook'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useRef, useState} from 'react'
import {Switch} from 'react-router-dom'
import RouteWithSubRoutes from 'routes/RouteWithSubRoutes'
import {getResponseItem, NullAsDefault, renderIf} from 'views/Shared'
import useAsync from '../../../modules/asyncCache/useAsync'
import {gPaths} from '../configs'
import {confirmOnSuccess} from '../factory/createRedirectConfirm'
import {bindQueryParam, mapPathParam} from '../functions/routerHelper'
import {PermissionEntity} from '../Permissions/PermissionEntity'
import CourseEntity from './CourseSet/CourseEntity'
import {courseRouteEntities} from './CourseSet/courseRouteEntities'
import {routes} from './routes'
import {useCourse} from './useCourse'
import {useRouteSearchParam} from "../../../envs/_current/history";
import {CourseActionTypes} from "./index";

const CourseContext = React.createContext({})

const Provider = ({
                    item,
                    children,
                    initialCurrent,
                    routePathField = 'path',
                    routeEntities = courseRouteEntities,
                    ...props
                  }) => {
  const t = useTranslate()

  const formRef = useRef()

  const [current, setCurrent] = useState({
    type: initialCurrent,
    value: undefined,
  })

  const id = getId(item) ?? item

  const apiInfo = course_getCourseById_Api

  const cacheId = mapPathParam(apiInfo?.path, {id})

  const {response, isLoading = false} = useAsync({
    apiInfo,
    query: bindQueryParam({id}),
  })

  const isSucceed = Number(_.get(response, 'status')) === 200

  const cache = getResponseItem(response)

  const error = getResponseItem(response, 'error')

  const errorMessage = _.get(response, 'errorMessages.error')

  const error_code = _.get(response, 'errorMessages.error_code')

  const status = {
    title: getResponseItem(response, 'status'),
    description: errorMessage,
  }

  const defaultRoute = _.first(routeEntities)

  const getDefaultAction = (current) => {
    switch (current) {
      case gPaths.course:
        return _.get(defaultRoute, routePathField)
      default:
        return null
    }
  }

  const isValidRoute = useCallback(
    (route) => {
      return routeEntities
        .map((item) => _.get(item, routePathField))
        .includes(route)
    },
    [routeEntities, routePathField]
  )

  const renderHeader = useCallback((action, isModal) => null, [])

  const onDismiss = () => {
    setCurrent(null)
  }

  const {
    updateAsync,
    onUpdate = NullAsDefault,
    onDuplicate = NullAsDefault,
    onRegister = NullAsDefault,
  } = useCourse({
    onRegistered: (result, data) => {
      onDismiss()
      const item = getResponseItem(data?.response)
      const {course} = item ?? {}
      confirmOnSuccess({
        icon: null,
        translate: t,
        title: 'successfully registered',
        description: 'successfully registered description',
        content: (
          <React.Fragment>
            {t('you have successfully registered for the course')}
            <span className="ml-2 font-bold text-color-000">
              {getTitle(course)}
            </span>
          </React.Fragment>
        ),
      })
    },
    onDuplicated: (result, data) => {
      onDismiss()
      const item = getResponseItem(data?.response)
      const {course} = item ?? {}
      confirmOnSuccess({
        translate: t,
        title: "You have successfully copied",
        description: null,
        content: (
            <React.Fragment>
              {t("You have successfully copied the new course")}
              <span className="ml-2 font-bold text-color-000">
              {getTitle(course)}
            </span>
            </React.Fragment>
        ),
      })
    },
  })

  const handleAction = (action, params) => {
    switch (action) {
      case CourseActionTypes.SHOW_ENROLLMENT_REGISTER_MODAL:
        setCurrent({type: routes.register.path, value: params})
        break
      case CourseActionTypes.REGISTER:
        onRegister(params, {id})
        break
      case CourseActionTypes.COURSE_UPDATE:
        onUpdate(params, {id})
        break
      case CourseActionTypes.COURSE_COPY:
        onDuplicate(params, {id})
        break
      default:
        break
    }
  }

  const renderWarnings = useCallback((data) => {
    const warnings = Array.from(data ?? [])
    return renderIf(
      warnings.length,
      warnings.map((item, index) => {
        const {title, description} = item ?? {}
        return (
          <div
            key={index}
            className="flex flex-col gap-3 p-3 border border-dashed border-orange-500 rounded-lg">
            <div className="flex flex-col items-center">
              <div className="font-semibold text-sm text-orange-600">
                {title}
              </div>
              <div className="text-xs text-color-400 italic">{description}</div>
            </div>
          </div>
        )
      })
    )
  }, [])

  return (
    <CourseEntity
      key={cacheId}
      item={id}>
      {(item) => (
        <PermissionEntity item={item}>
          <CourseContext.Provider
            value={{
              id,
              item,
              error,
              cache,
              status,
              current,
              formRef,
              isLoading,
              isSucceed,
              error_code,
              updateAsync,
              setCurrent,
              renderHeader,
              isValidRoute,
              handleAction,
              errorMessage,
              defaultRoute,
              routeEntities,
              routePathField,
              renderWarnings,
              getDefaultAction,
              ...props,
            }}>
            <React.Fragment>
              {children}
              {current?.type && (
                <Switch
                  location={{
                    hash: '',
                    search: '',
                    key: current?.type,
                    state: current?.value,
                    pathname: current?.type,
                  }}>
                  {Object.values(routes).map((route, i) => (
                    <RouteWithSubRoutes
                      key={i}
                      {...route}
                    />
                  ))}
                </Switch>
              )}
            </React.Fragment>
          </CourseContext.Provider>
        </PermissionEntity>
      )}
    </CourseEntity>
  )
}

export const CourseProvider = (props) => (
  <UseHook
    hook={useRouteSearchParam}
    deps={['initialCurrent']}>
    {(initialCurrent) => (
      <Provider
        initialCurrent={initialCurrent}
        {...props}
      />
    )}
  </UseHook>
)

export default CourseContext
